import { FormControlProps } from "@mui/material";
import { Control, FieldValues, Path, useFormContext, useWatch } from "react-hook-form";
import { useMemo } from "react";

import { TGetProvincesAndMunicipalitiesResDto } from "@/modules/irohana-components/api/services/global/constant/dtos/get-provinces-municipalities.dto";
import { FormField } from "@/modules/irohana-components/elements/FormField";
import { Input } from "@/modules/irohana-components/elements/Input";
import { Select } from "@/modules/irohana-components/elements/Select";
import { TOption } from "@/modules/irohana-components/types/common";

export type TCityInputProps<T extends FieldValues> = {
  name: Path<T>;
  control?: Control<T>;
  zipCodeFieldName?: Path<T>;
  zipCode?: string | null;
  locationInfo?: TGetProvincesAndMunicipalitiesResDto;
  inputPlaceholder?: string;
  selectPlaceholder?: string;
  required?: boolean;
  disabled?: boolean;
  label?: string;
  helper?: string;
  helperTile?: string;
  helperContent?: React.ReactElement;
  helperIcon?: React.ReactElement;
  errorMessage?: string;
  noLabel?: boolean;
  largeLabel?: boolean;
  errorTextAlign?: "left" | "right";
  breakLineLabel?: boolean;
  isMessageNowrap?: boolean;
} & Omit<FormControlProps, "children">;

export const CityInput = <T extends FieldValues>({
  name,
  control: controlProp,
  zipCodeFieldName,
  zipCode: zipCodeProp,
  locationInfo,
  inputPlaceholder = "入力してください",
  selectPlaceholder = "選択してください",
  required,
  disabled,
  label,
  helper,
  helperTile,
  helperContent,
  helperIcon,
  errorMessage,
  noLabel,
  largeLabel,
  errorTextAlign,
  breakLineLabel,
  isMessageNowrap,
  ...formControlProps
}: TCityInputProps<T>) => {
  const context = controlProp ? null : useFormContext<T>();
  const control = controlProp ?? context?.control;

  const currentCity = useWatch({ control, name });
  const zipCodeFromForm = zipCodeFieldName && control ? useWatch({ control, name: zipCodeFieldName as Path<T> }) : undefined;

  const zipCode = zipCodeProp ?? zipCodeFromForm;

  const cityOptions = useMemo<TOption[]>(() => {
    const options: TOption[] = [];

    if (zipCode && locationInfo?.cities && Array.isArray(locationInfo.cities) && locationInfo.cities.length > 0) {
      locationInfo.cities.forEach((city) => {
        options.push({
          label: city,
          value: city,
        });
      });
    }

    if (currentCity && typeof currentCity === "string" && currentCity.trim() !== "") {
      const cityExists = options.some((option) => option.value === currentCity);
      if (!cityExists) {
        options.push({
          label: currentCity,
          value: currentCity,
        });
      }
    }

    return options;
  }, [locationInfo?.cities, currentCity, zipCode]);

  const hasCitiesOptions = zipCode && locationInfo?.cities && Array.isArray(locationInfo.cities) && locationInfo.cities.length > 1;
  const hasSingleCity = cityOptions.length === 1;

  return (
    <FormField
      control={control}
      name={name}
      required={required}
      label={label}
      helper={helper}
      helperTile={helperTile}
      helperContent={helperContent}
      helperIcon={helperIcon}
      errorMessage={errorMessage}
      noLabel={noLabel}
      largeLabel={largeLabel}
      errorTextAlign={errorTextAlign}
      breakLineLabel={breakLineLabel}
      isMessageNowrap={isMessageNowrap}
      {...formControlProps}
    >
      {hasCitiesOptions ? (
        <Select placeholder={selectPlaceholder} options={cityOptions} readonly={hasSingleCity} disabled={disabled} clearable={false} />
      ) : (
        <Input placeholder={inputPlaceholder} readonly disabled={disabled} />
      )}
    </FormField>
  );
};
